import { Outlet } from 'react-router-dom';
import { Menu, AlertTriangle } from 'lucide-react';
import { Component, ReactNode, useState, useEffect } from 'react';
import OrganizationSwitcher from '../common/OrganizationSwitcher';
import { NavigationSidebar } from '../navigation/NavigationSidebar';
import { useNavigationStore } from '../../store/navigationStore';
import TopHeader from './TopHeader';
import { HelpSlideOut } from '../help/HelpSlideOut';
import { ChatSlideOut } from '../help/ChatSlideOut';
import NotificationsSlideOut from '../common/NotificationsSlideOut';
import { getRateLimitRemainingMs, onRateLimitChange } from '../../services/api';

// Catches render errors so the whole page doesn't go blank
class PageErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="m-8 p-6 bg-red-50 border border-red-200 rounded-lg">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Page Error</h2>
          <p className="text-sm text-red-700 font-mono whitespace-pre-wrap">
            {(this.state.error as Error).message}
          </p>
          <button
            onClick={() => this.setState({ error: null })}
            className="mt-4 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Dismiss
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Counts down to zero and returns the remaining seconds. */
function useRateLimitCountdown() {
  const [remainingMs, setRemainingMs] = useState(() => getRateLimitRemainingMs());

  useEffect(() => {
    // Subscribe to state changes triggered by the response interceptor
    const unsub = onRateLimitChange(() => {
      setRemainingMs(getRateLimitRemainingMs());
    });

    // Tick every second while limited so the countdown updates
    const tick = setInterval(() => {
      const ms = getRateLimitRemainingMs();
      setRemainingMs(ms);
      if (ms === 0) clearInterval(tick);
    }, 1000);

    return () => { unsub(); clearInterval(tick); };
  }, []);

  return Math.ceil(remainingMs / 1000);
}

export default function MainLayout() {
  const { sidebarCollapsed, mobileSidebarOpen, setMobileSidebarOpen } = useNavigationStore();
  const [helpOpen, setHelpOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const rateLimitSec = useRateLimitCountdown();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-metallic-950">
      {/* Mobile sidebar backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Navigation Sidebar - Metadata-driven */}
      <NavigationSidebar />

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Rate-limit banner — shown when the API is throttling us */}
        {rateLimitSec > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300 dark:border-amber-600 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              <strong>API rate limit reached.</strong> Requests are paused — resuming in{' '}
              <strong>{rateLimitSec}s</strong>. Please wait; refreshing will not help.
            </span>
          </div>
        )}
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white dark:bg-metallic-900 border-b dark:border-metallic-800">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1 lg:hidden" />

            {/* Organization Switcher (desktop) */}
            <div className="hidden lg:flex items-center">
              <OrganizationSwitcher />
            </div>

            {/* Organization Switcher (mobile) */}
            <div className="flex lg:hidden items-center">
              <OrganizationSwitcher />
            </div>

            {/* Top Header - Help, Chat, Notifications, Profile */}
            <TopHeader
              className="ml-auto"
              onHelpClick={() => { setNotificationsOpen(false); setChatOpen(false); setHelpOpen(h => !h); }}
              onChatClick={() => { setHelpOpen(false); setNotificationsOpen(false); setChatOpen(c => !c); }}
              onNotificationsClick={() => { setHelpOpen(false); setChatOpen(false); setNotificationsOpen(n => !n); }}
              notificationCount={unreadCount}
              chatIsOpen={chatOpen}
              helpIsOpen={helpOpen}
              notificationsIsOpen={notificationsOpen}
            />
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <PageErrorBoundary>
            <Outlet />
          </PageErrorBoundary>
        </main>

        {/* Help Slide-Out Panel */}
        <HelpSlideOut isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

        {/* Chat Slide-Out Panel */}
        <ChatSlideOut isOpen={chatOpen} onClose={() => setChatOpen(false)} />

        {/* Notifications Slide-Out Panel */}
        <NotificationsSlideOut
          isOpen={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          onUnreadCountChange={setUnreadCount}
        />
      </div>
    </div>
  );
}

// Made with Bob
